import * as gsap from "gsap";
import {Anchor} from "./Anchor";
import * as AssetHelpers from "./AssetHelpers";
import {Container} from "./Container";
import {IPoint} from "./Geom";
import {Signal} from "./Signal";
import {Sprite} from "./Sprite";

export type ButtonEventListener<T> = (button: T, event?: PIXI.InteractionEvent) => void;

export class BaseButton extends Container {

    public readonly anchor: IPoint = {x: 0.5, y: 0.5};
    public readonly visuals = this.addChild(new Container());
    public toggleOnDown = false;

    public readonly onOut = Signal<ButtonEventListener<BaseButton>>();
    public readonly onOver = Signal<ButtonEventListener<BaseButton>>();
    public readonly onDown = Signal<ButtonEventListener<BaseButton>>();
    public readonly onUp = Signal<ButtonEventListener<BaseButton>>();
    public readonly onClick = Signal<ButtonEventListener<BaseButton>>();
    public readonly onChange = Signal<(button: BaseButton, selected: boolean) => void>();

    private _isOver = false;
    private _isDown = false;
    private _enabled = true;
    private _toggle = false;
    private _selected = false;
    private _inactiveTimeout?: number;

    constructor() {
        super();

        this.buttonMode = true;
        this.interactive = true;
        this.interactiveChildren = true;

        this.on("pointerup", this.onPointerUp.bind(this));
        this.on("pointerupoutside", this.onPointerOut.bind(this));
        this.on("pointerover", this.onPointerOver.bind(this));
        this.on("pointerout", this.onPointerOut.bind(this));
        this.on("pointerdown", this.onPointerDown.bind(this));
    }

    get isOver(): boolean {
        return this._isOver;
    }

    get isDown(): boolean {
        return this._isDown;
    }

    get enabled(): boolean {
        return this._enabled;
    }

    set enabled(value: boolean) {
        if (this._enabled !== value) {
            this._enabled = value;
            this.buttonMode = value;
            this.interactive = value;
            this.updateState();
            this._isDown = false;
            if (this._isOver) {
                this._isOver = false;
                this.onOut(this, undefined);
            }
        }
    }

    get toggle(): boolean {
        return this._toggle;
    }

    set toggle(value: boolean) {
        if (this._toggle !== value) {
            this._toggle = value;
            if (!this._toggle && this._selected) {
                this._selected = false;
                this.updateState();
            }
        }
    }

    get selected(): boolean {
        return this._selected;
    }

    set selected(value: boolean) {
        if (this._toggle && this._selected !== value) {
            this._selected = value;
            this.updateState();
        }
    }

    public updateState(): void {
        // override
    }

    public destroy(): void {
        this.onOut.remove();
        this.onOver.remove();
        this.onDown.remove();
        this.onUp.remove();
        this.onClick.remove();
        this.onChange.remove();
        if (this._inactiveTimeout !== undefined) {
            clearTimeout(this._inactiveTimeout);
        }
        super.destroy();
    }

    protected onPointerUp(event: PIXI.InteractionEvent): void {
        if (this._inactiveTimeout !== undefined) {
            return;
        }
        const wasDown = this._isDown;
        this._isDown = false;
        this.onUp(this, event);
        if (wasDown && this._toggle && !this.toggleOnDown) {
            this._selected = !this._selected;
            this.updateState();
            this.onClick(this, event);
            this.onChange(this, this._selected);
            this.startInactiveTimeout();
        } else if (wasDown) {
            this.updateState();
            this.onClick(this, event);
            this.startInactiveTimeout();
        }
    }

    protected onPointerOver(event: PIXI.InteractionEvent): void {
        if (this._inactiveTimeout !== undefined) {
            return;
        }
        if (!this._isOver) {
            this._isOver = true;
            this.updateState();
            this.onOver(this, event);
        }
    }

    protected onPointerOut(event: PIXI.InteractionEvent): void {
        if (this._isOver || this._isDown) {
            this._isOver = false;
            this._isDown = false;
            this.updateState();
            this.onOut(this, event);
        }
    }

    protected onPointerDown(event: PIXI.InteractionEvent): void {
        if (this._inactiveTimeout !== undefined) {
            return;
        }
        if (!this._isDown) {
            this._isDown = true;
            if (this.toggleOnDown && this._toggle) {
                this._selected = !this._selected;
                this.updateState();
                this.onDown(this, event);
                this.onChange(this, this._selected);
            } else {
                this.updateState();
                this.onDown(this, event);
            }
        }
    }

    private startInactiveTimeout() {
        this._inactiveTimeout = window.setTimeout(() => {
            this._inactiveTimeout = undefined;
        }, 200);
    }
}

export class TextureButton extends BaseButton {

    public readonly sprite = this.visuals.addChild(new Sprite());

    private _wasSelected = false;

    constructor(texture?: string | PIXI.Texture,
                onClick?: ButtonEventListener<BaseButton>,
                anchor?: IPoint) {
        super();
        this.setLayoutTexture(texture || PIXI.Texture.EMPTY, anchor ? anchor.x : 0.5, anchor ? anchor.y : 0.5);
        if (onClick) {
            this.onClick.add(onClick);
        }
    }

    public setLayoutTexture(texture: string | PIXI.Texture, anchorX: number, anchorY: number) {
        this.sprite.texture = (texture instanceof PIXI.Texture ? texture :
            texture ? AssetHelpers.texture(texture) : PIXI.Texture.EMPTY) || PIXI.Texture.EMPTY;
        this.anchor.x = this.sprite.anchor.x = anchorX;
        this.anchor.y = this.sprite.anchor.y = anchorY;
    }

    public updateState(): void {
        super.updateState();

        if (this.toggle && this.selected !== this._wasSelected) {
            this._wasSelected = this.selected;

            let textureName = this.sprite.texture.textureCacheIds[0];
            if (textureName) {
                textureName = textureName.replace(/-(on|off)/, this.selected ? "-on" : "-off");
                const texture = AssetHelpers.texture(textureName);
                if (texture) {
                    this.sprite.texture = texture;
                }
            }
        }
    }
}

export interface IHoverableButton {
    hoverDisabled?: boolean;
    downDisabled?: boolean;
    hoverScale?: number;
    downScale?: number;
    hoverAnchor?: IPoint;
}

export function HoverableButton(scale: number | [number, number] = [1.05, 0.9], anchor: IPoint = Anchor.middleCenter) {
    return function (this: BaseButton & IHoverableButton) {
        let tween: gsap.TweenLite;
        let init = true;
        let startWidth = NaN;
        let startHeight = NaN;
        let startScaleX = NaN;
        let startScaleY = NaN;
        const hoverScale = typeof scale === "number" ? scale : scale[0];
        const downScale = typeof scale === "number" ? (2 - scale) : scale[1];
        const initScale = () => {
            if (init) {
                startWidth = this.visuals.width;
                startHeight = this.visuals.height;
                startScaleX = this.visuals.scaleX;
                startScaleY = this.visuals.scaleY;
                init = false;
            }
        };
        const onOver = () => {
            if (PIXI.utils.isMobile.any) {
                return;
            }
            if (tween) {
                tween.kill();
            }
            if (!this.hoverDisabled) {
                initScale();
                const a = this.hoverAnchor || anchor;
                const s = this.hoverScale || hoverScale;
                tween = gsap.TweenLite.to(this.visuals, 0.15, {
                    x: a ? (this.anchor.x - a.x) * startWidth * (s - 1) : 0,
                    y: a ? (this.anchor.y - a.y) * startHeight * (s - 1) : 0,
                    scaleX: startScaleX * s,
                    scaleY: startScaleY * s,
                    ease: gsap.Sine.easeOut,
                });
            }
        };
        const onOut = () => {
            if (tween) {
                tween.kill();
            }
            if (!this.hoverDisabled) {
                initScale();
                tween = gsap.TweenLite.to(this.visuals, 0.15, {
                    x: 0,
                    y: 0,
                    scaleX: startScaleX,
                    scaleY: startScaleY,
                    ease: gsap.Sine.easeOut,
                });
            }
        };
        const onDown = () => {
            if (tween) {
                tween.kill();
            }
            if (!this.downDisabled) {
                initScale();
                const a = this.hoverAnchor || anchor;
                const s = this.downScale || downScale;
                tween = gsap.TweenLite.to(this.visuals, 0.15, {
                    x: a ? (this.anchor.x - a.x) * startWidth * (s - 1) : 0,
                    y: a ? (this.anchor.y - a.y) * startHeight * (s - 1) : 0,
                    scaleX: startScaleX * s,
                    scaleY: startScaleY * s,
                    ease: gsap.Sine.easeOut,
                    onComplete: () => {
                        if (this.isOver && !this.isDown) {
                            onOver();
                        } else {
                            onOut();
                        }
                    },
                });
            }
        };
        this.onOver.add(onOver);
        this.onDown.add(onDown);
        this.onOut.add(onOut);
        return this;
    };
}
